import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CameraCapture } from "@/components/CameraCapture";
import { FileUploadField } from "@/components/FileUploadField";
import { Check, ChevronLeft, Loader2, UserRound, ShieldCheck, Users, Landmark, Contact2, Plus } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const STEPS = [
  { key: 1, label: "Personal", Icon: UserRound },
  { key: 2, label: "KYC", Icon: ShieldCheck },
  { key: 3, label: "References", Icon: Users },
  { key: 4, label: "Bank", Icon: Landmark },
];

const Field = ({ label, children }) => (
  <div><Label className="mb-1.5 block">{label}</Label>{children}</div>
);

export default function Apply() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [personal, setPersonal] = useState({
    full_name: "", dob: "", gender: "", address: "", city: "", state: "",
    pincode: "", employment_type: "", monthly_income: "",
  });
  const [kyc, setKyc] = useState({ aadhaar_number: "", pan_number: "", aadhaar_file_id: "", pan_file_id: "", selfie_file_id: "" });
  const [refs, setRefs] = useState([
    { _k: "ref-0", name: "", phone: "", relationship: "" },
    { _k: "ref-1", name: "", phone: "", relationship: "" },
    { _k: "ref-2", name: "", phone: "", relationship: "" },
  ]);
  const [bank, setBank] = useState({ account_holder: "", bank_name: "", account_number: "", ifsc: "", proof_file_id: "" });

  const setP = (k) => (e) => setPersonal({ ...personal, [k]: e.target.value });
  const setB = (k) => (e) => setBank({ ...bank, [k]: e.target.value });
  const setR = (i, k) => (e) => { const c = [...refs]; c[i] = { ...c[i], [k]: e.target.value }; setRefs(c); };

  const validateStep = () => {
    if (step === 1) {
      const req = ["full_name", "dob", "gender", "address", "city", "state", "pincode", "employment_type", "monthly_income"];
      for (const k of req) if (!String(personal[k]).trim()) { toast.error("Please fill all personal details"); return false; }
      if (!/^\d{6}$/.test(personal.pincode)) { toast.error("Pincode must be 6 digits"); return false; }
    }
    if (step === 2) {
      if (!/^\d{12}$/.test(kyc.aadhaar_number.replace(/\s/g, ""))) { toast.error("Aadhaar must be 12 digits"); return false; }
      if (!/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(kyc.pan_number)) { toast.error("Enter a valid PAN (e.g. ABCDE1234F)"); return false; }
      if (!kyc.aadhaar_file_id || !kyc.pan_file_id || !kyc.selfie_file_id) { toast.error("Please capture Aadhaar, PAN and Selfie"); return false; }
    }
    if (step === 3) {
      for (const r of refs) {
        if (!r.name.trim() || !/^\d{10}$/.test(r.phone.trim())) { toast.error("Each reference needs a name and 10-digit phone"); return false; }
      }
    }
    if (step === 4) {
      const req = ["account_holder", "bank_name", "account_number", "ifsc"];
      for (const k of req) if (!String(bank[k]).trim()) { toast.error("Please fill all bank details"); return false; }
      if (!/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(bank.ifsc)) { toast.error("Enter a valid IFSC code"); return false; }
      if (!bank.proof_file_id) { toast.error("Please upload bank proof"); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(4, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const pickContacts = async () => {
    if ("contacts" in navigator && "ContactsManager" in window) {
      try {
        const selected = await navigator.contacts.select(["name", "tel"], { multiple: true });
        if (selected && selected.length) {
          const mapped = selected.slice(0, 3).map((c) => ({
            name: (c.name && c.name[0]) || "", phone: ((c.tel && c.tel[0]) || "").replace(/\D/g, "").slice(-10), relationship: "",
          }));
          const c = [...refs];
          mapped.forEach((m, i) => { c[i] = { ...c[i], ...m }; });
          setRefs(c);
          toast.success(`Added ${mapped.length} contact(s)`);
        }
      } catch {
        toast.error("Contact picker was cancelled or is unavailable. Please enter manually.");
      }
    } else {
      toast.message("Contact picker not supported on this device. Please enter references manually.");
    }
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        personal: { ...personal, monthly_income: parseFloat(personal.monthly_income) },
        kyc: { ...kyc, pan_number: kyc.pan_number.toUpperCase(), aadhaar_number: kyc.aadhaar_number.replace(/\s/g, "") },
        references: refs,
        bank: { ...bank, ifsc: bank.ifsc.toUpperCase() },
      };
      const { data } = await api.post("/applications", payload);
      toast.success("Application submitted successfully");
      nav("/submitted", { state: data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-border">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo />
          <button onClick={() => nav("/")} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => {
              const active = step === s.key; const done = step > s.key;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex flex-col items-center gap-1" data-testid={`wizard-step-${s.key}`}>
                    <div className={`h-9 w-9 grid place-items-center rounded-full text-sm font-semibold transition-colors ${done ? "bg-brand text-white" : active ? "bg-brand text-white ring-4 ring-brand-light" : "bg-slate-100 text-slate-400"}`}>
                      {done ? <Check className="h-4 w-4" /> : <s.Icon className="h-4 w-4" />}
                    </div>
                    <span className={`text-[11px] font-medium ${active ? "text-brand" : "text-muted-foreground"}`}>{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${step > s.key ? "bg-brand" : "bg-slate-200"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">Step {step} of 4</p>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.2 }}>
            {step === 1 && (
              <Card className="p-5 sm:p-6 space-y-4 ip-card-shadow">
                <h2 className="text-xl font-bold tracking-tight">Personal Details</h2>
                <Field label="Full name"><Input data-testid="personal-full-name" value={personal.full_name} onChange={setP("full_name")} /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Date of birth"><Input type="date" data-testid="personal-dob" value={personal.dob} onChange={setP("dob")} /></Field>
                  <Field label="Gender">
                    <Select value={personal.gender} onValueChange={(v) => setPersonal({ ...personal, gender: v })}>
                      <SelectTrigger data-testid="personal-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Address"><Input data-testid="personal-address" value={personal.address} onChange={setP("address")} /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="City"><Input data-testid="personal-city" value={personal.city} onChange={setP("city")} /></Field>
                  <Field label="State"><Input data-testid="personal-state" value={personal.state} onChange={setP("state")} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Pincode"><Input data-testid="personal-pincode" inputMode="numeric" maxLength={6} value={personal.pincode} onChange={setP("pincode")} /></Field>
                  <Field label="Monthly income (₹)"><Input data-testid="personal-income" inputMode="numeric" value={personal.monthly_income} onChange={setP("monthly_income")} /></Field>
                </div>
                <Field label="Employment type">
                  <Select value={personal.employment_type} onValueChange={(v) => setPersonal({ ...personal, employment_type: v })}>
                    <SelectTrigger data-testid="personal-employment"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Salaried">Salaried</SelectItem>
                      <SelectItem value="Self-employed">Self-employed</SelectItem>
                      <SelectItem value="Business">Business</SelectItem>
                      <SelectItem value="Student">Student</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </Card>
            )}

            {step === 2 && (
              <Card className="p-5 sm:p-6 space-y-4 ip-card-shadow">
                <h2 className="text-xl font-bold tracking-tight">KYC & Documents</h2>
                <div className="grid grid-cols-1 gap-4">
                  <Field label="Aadhaar number"><Input data-testid="kyc-aadhaar-input" inputMode="numeric" maxLength={12} placeholder="12-digit Aadhaar" value={kyc.aadhaar_number} onChange={(e) => setKyc({ ...kyc, aadhaar_number: e.target.value })} /></Field>
                  <Field label="PAN number"><Input data-testid="kyc-pan-input" maxLength={10} placeholder="ABCDE1234F" value={kyc.pan_number} onChange={(e) => setKyc({ ...kyc, pan_number: e.target.value.toUpperCase() })} /></Field>
                </div>
                <p className="text-xs text-muted-foreground">For your security, identity documents and selfie are captured with your camera only.</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <CameraCapture label="Aadhaar Photo" purpose="aadhaar" facingMode="environment" testId="capture-aadhaar" onCaptured={(id) => setKyc((k) => ({ ...k, aadhaar_file_id: id }))} />
                  <CameraCapture label="PAN Photo" purpose="pan" facingMode="environment" testId="capture-pan" onCaptured={(id) => setKyc((k) => ({ ...k, pan_file_id: id }))} />
                  <CameraCapture label="Selfie" purpose="selfie" facingMode="user" testId="capture-selfie" onCaptured={(id) => setKyc((k) => ({ ...k, selfie_file_id: id }))} />
                </div>
              </Card>
            )}

            {step === 3 && (
              <Card className="p-5 sm:p-6 space-y-4 ip-card-shadow">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-xl font-bold tracking-tight">3 Reference Contacts</h2>
                  <Button type="button" variant="outline" size="sm" onClick={pickContacts} data-testid="select-contacts-button">
                    <Contact2 className="h-4 w-4 mr-1.5" /> Select from Contacts
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">We only submit the 3 contacts you choose. Your full address book is never uploaded.</p>
                {refs.map((r, i) => (
                  <div key={r._k} className="rounded-xl border border-border p-4 space-y-3" data-testid={`reference-${i}`}>
                    <p className="text-sm font-semibold text-brand">Reference {i + 1}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Name"><Input data-testid={`reference-${i}-name`} value={r.name} onChange={setR(i, "name")} /></Field>
                      <Field label="Phone"><Input data-testid={`reference-${i}-phone`} inputMode="numeric" maxLength={10} value={r.phone} onChange={setR(i, "phone")} /></Field>
                    </div>
                    <Field label="Relationship (optional)"><Input data-testid={`reference-${i}-relationship`} value={r.relationship} onChange={setR(i, "relationship")} placeholder="e.g. Friend, Colleague" /></Field>
                  </div>
                ))}
              </Card>
            )}

            {step === 4 && (
              <Card className="p-5 sm:p-6 space-y-4 ip-card-shadow">
                <h2 className="text-xl font-bold tracking-tight">Bank Details</h2>
                <Field label="Account holder name"><Input data-testid="bank-holder" value={bank.account_holder} onChange={setB("account_holder")} /></Field>
                <Field label="Bank name"><Input data-testid="bank-name" value={bank.bank_name} onChange={setB("bank_name")} /></Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Account number"><Input data-testid="bank-account" inputMode="numeric" value={bank.account_number} onChange={setB("account_number")} /></Field>
                  <Field label="IFSC code"><Input data-testid="bank-ifsc" maxLength={11} value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} placeholder="ABCD0123456" /></Field>
                </div>
                <Field label="Bank proof (passbook / statement / cancelled cheque)">
                  <FileUploadField label="Upload bank proof" purpose="bank_proof" testId="bank-proof-upload" onUploaded={(id) => setBank((b) => ({ ...b, proof_file_id: id }))} />
                </Field>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="sticky bottom-0 z-30 -mx-4 mt-6 flex gap-3 border-t border-border bg-background/95 px-4 py-3 backdrop-blur-sm">
          {step > 1 && <Button variant="outline" onClick={back} className="h-11" data-testid="wizard-back-button"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>}
          {step < 4 ? (
            <Button onClick={next} className="flex-1 h-11" data-testid="wizard-next-button">Next</Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="flex-1 h-11 bg-gold hover:bg-gold-dark text-white" data-testid="wizard-submit-button">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Submit Application
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
